import React, { useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { SectionHeading } from "@/components/SectionHeading";
import { MediaTile } from "@/components/MediaTile";
import { MediaLightbox } from "@/components/MediaLightbox";
import { ArrivalsVideoSpotlight } from "@/components/ArrivalsVideoSpotlight";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { useProducts } from "@/hooks/useProducts";
import { buildWhatsAppLink, categoryEnquireMessage } from "@/lib/whatsapp";
import { Expand, Video } from "lucide-react";

function OfferDetailCard({ item }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const hasMedia = Boolean(item.media_url);
  const extraMediaCount = (item.gallery_images?.length || 0) + (item.gallery_videos?.length || 0);

  return (
    <div className="card-lux rounded-2xl overflow-hidden flex flex-col h-full" data-testid={`new-arrival-card-${item.id}`}>
      {hasMedia && (
        <button
          type="button"
          onClick={() => setLightboxOpen(true)}
          className="group relative aspect-[4/3] w-full overflow-hidden bg-muted text-left"
          aria-label={`View all photos of ${item.name}`}
          data-testid={`new-arrival-media-btn-${item.id}`}
        >
          <MediaTile
            src={item.media_url}
            mediaType={item.media_type}
            alt={item.name}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

          {item.media_type === "video" && (
            <span className="absolute top-3 left-3 h-8 w-8 rounded-full bg-background/80 backdrop-blur flex items-center justify-center border border-primary/30">
              <Video className="h-3.5 w-3.5 text-primary" />
            </span>
          )}

          <span className="absolute bottom-3 right-3 rounded-full bg-background/85 backdrop-blur px-2.5 py-1 text-[11px] font-medium text-foreground border border-primary/30 flex items-center gap-1">
            <Expand className="h-3 w-3 text-primary" /> {extraMediaCount > 0 ? `View all (${extraMediaCount + 1})` : "View full size"}
          </span>
        </button>
      )}
      <div className="p-6 sm:p-7 flex flex-col flex-1">
        <h3 className="font-heading text-xl font-semibold text-foreground">{item.name}</h3>
        {item.status_text && <p className="mt-2 font-heading text-base text-primary">{item.status_text}</p>}
        {item.description && (
          <p className="mt-3 text-sm text-muted-foreground leading-relaxed flex-1">{item.description}</p>
        )}
        <Button asChild variant="whatsapp" className="mt-6" data-testid={`new-arrival-enquire-btn-${item.id}`}>
          <a
            href={buildWhatsAppLink(categoryEnquireMessage(item.name))}
            target="_blank"
            rel="noopener noreferrer"
          >
            <i className="fa-brands fa-whatsapp" /> Enquire on WhatsApp
          </a>
        </Button>
      </div>

      {hasMedia && <MediaLightbox item={item} open={lightboxOpen} onOpenChange={setLightboxOpen} />}
    </div>
  );
}

export default function NewArrivals() {
  const { items: offers, loading: loadingOffers } = useProducts("offers");
  const { items: festival, loading: loadingFestival } = useProducts("festival");

  const loading = loadingOffers || loadingFestival;
  const items = [...offers, ...festival]
    .filter((p) => p.is_active)
    .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <main className="pt-32 pb-20">
        <ArrivalsVideoSpotlight />
        <section className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="New Arrivals & Offers"
            title="Latest Offers & Festival Specials"
            subtitle="Everything currently live — festive collections, exchange offers & special pricing. Tap any item to enquire directly on WhatsApp."
          />

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="new-arrivals-loading-state">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-80 rounded-2xl" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-20" data-testid="new-arrivals-empty-state">
              <p className="font-heading text-xl text-muted-foreground">No live offers right now — check back soon!</p>
              <Button asChild variant="premium" className="mt-6">
                <a
                  href={buildWhatsAppLink(categoryEnquireMessage("upcoming Offers"))}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fa-brands fa-whatsapp" /> Ask About Offers
                </a>
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="new-arrivals-grid">
              {items.map((item) => (
                <OfferDetailCard key={item.id} item={item} />
              ))}
            </div>
          )}
        </section>
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
